import { StandardEvents } from '@shopify/events';

const CONFIG_SELECTOR = 'script[data-lunqo-3d-config]';
const PRODUCT_MODEL_SELECTOR = 'product-model[data-lunqo-3d-product-id]';
const MATERIAL_SLOT_PREFIX = 'LUNQO_COLOR_SLOT_';
const FIXED_MATERIAL = 'LUNQO_FIXED';
const FALLBACK_ROUGHNESS = 0.72;
const FALLBACK_METALLIC = 0;
const scopeStates = new WeakMap();
let scheduled = false;

const BAMBU_FILAMENT_COLORS = Object.freeze({
  'jade white': '#FFFFFF',
  magenta: '#EC008C',
  gold: '#E4BD68',
  'mistletoe green': '#3F8E43',
  red: '#C12E1F',
  purple: '#5E43B7',
  beige: '#F7E6DE',
  pink: '#F55A74',
  'sunflower yellow': '#FEC600',
  bronze: '#847D48',
  turquoise: '#00B1B7',
  'indigo purple': '#482960',
  'light gray': '#D1D3D5',
  'hot pink': '#F5547C',
  yellow: '#F4EE2A',
  'cocoa brown': '#6F5034',
  cyan: '#0086D6',
  'blue grey': '#5B6579',
  silver: '#A6A9AA',
  orange: '#FF6A13',
  'bright green': '#BECF00',
  brown: '#9D432C',
  blue: '#0A2989',
  'dark gray': '#545454',
  gray: '#8E9089',
  'pumpkin orange': '#FF9016',
  'bambu green': '#00AE42',
  'maroon red': '#9D2235',
  'cobalt blue': '#0056B8',
  black: '#000000',
  'matte desert tan': '#E8DBB7',
  'matte caramel': '#AE835B',
  'matte dark green': '#68724D',
});

const FILAMENT_COLOR_ALIASES = Object.freeze({
  white: 'jade white',
  grey: 'gray',
  'light grey': 'light gray',
  'dark grey': 'dark gray',
  green: 'bambu green',
  'olive green': 'matte dark green',
  'dark green': 'matte dark green',
  'desert tan': 'matte desert tan',
  caramel: 'matte caramel',
});

function normalize(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function escapeAttribute(value) {
  return String(value).replace(/["\\]/g, '\\$&');
}

function clamp(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.min(1, Math.max(0, parsed)) : fallback;
}

function validColor(value) {
  if (typeof value !== 'string' || !value.trim()) return null;
  if (!window.CSS?.supports) return /^#[0-9a-f]{3,8}$/i.test(value.trim()) ? value.trim() : null;
  return CSS.supports('color', value.trim()) ? value.trim() : null;
}

const colorFactorCache = new Map();

function srgbChannelToLinear(channel) {
  return channel <= 0.04045
    ? channel / 12.92
    : Math.pow((channel + 0.055) / 1.055, 2.4);
}

function srgbFactorToLinear(factor) {
  return [
    srgbChannelToLinear(factor[0]),
    srgbChannelToLinear(factor[1]),
    srgbChannelToLinear(factor[2]),
    factor[3],
  ];
}

function parseHexColor(value) {
  const hex = value.replace('#', '');
  if (![3, 4, 6, 8].includes(hex.length) || !/^[0-9a-f]+$/i.test(hex)) return null;

  const expanded =
    hex.length <= 4
      ? [...hex].map((character) => character + character).join('')
      : hex;
  const hasAlpha = expanded.length === 8;
  const channels = expanded.match(/.{2}/g)?.map((channel) => Number.parseInt(channel, 16) / 255);
  if (!channels || channels.some((channel) => !Number.isFinite(channel))) return null;

  return [
    channels[0],
    channels[1],
    channels[2],
    hasAlpha ? channels[3] : 1,
  ];
}

function parseRgbColor(value) {
  const match = value.match(/^rgba?\((.*)\)$/i);
  if (!match) return null;

  const [colorPart, alphaPart] = match[1].split('/').map((part) => part.trim());
  const components = colorPart.includes(',')
    ? colorPart.split(',').map((part) => part.trim())
    : colorPart.split(/\s+/);
  if (components.length < 3) return null;

  const parseChannel = (channel) =>
    channel.endsWith('%') ? Number.parseFloat(channel) / 100 : Number.parseFloat(channel) / 255;
  const parseAlpha = (alpha) =>
    !alpha ? 1 : alpha.endsWith('%') ? Number.parseFloat(alpha) / 100 : Number.parseFloat(alpha);
  const inlineAlpha = components.length > 3 ? components[3] : null;
  const factor = [
    parseChannel(components[0]),
    parseChannel(components[1]),
    parseChannel(components[2]),
    parseAlpha(alphaPart || inlineAlpha),
  ];

  return factor.every(Number.isFinite)
    ? factor.map((channel) => Math.min(1, Math.max(0, channel)))
    : null;
}

function colorToFactor(value) {
  if (typeof value !== 'string') return null;
  const color = value.trim();
  if (!color) return null;

  const cached = colorFactorCache.get(color);
  if (cached) return [...cached];

  let factor = color.startsWith('#') ? parseHexColor(color) : parseRgbColor(color);

  if (!factor && document.body) {
    const probe = document.createElement('span');
    probe.style.color = color;
    if (probe.style.color) {
      probe.hidden = true;
      document.body.appendChild(probe);
      factor = parseRgbColor(getComputedStyle(probe).color);
      probe.remove();
    }
  }

  if (!factor) return null;
  const linearFactor = srgbFactorToLinear(factor);
  colorFactorCache.set(color, linearFactor);
  return [...linearFactor];
}

function parseConfig(script) {
  try {
    const parsed = JSON.parse(script.textContent || '{}');
    const custom = parsed.custom && typeof parsed.custom === 'object' ? parsed.custom : {};
    return {
      productId: String(parsed.productId || script.dataset.productId || ''),
      mapping: { ...(parsed.defaultMapping || {}), ...(custom.mapping || parsed.mapping || {}) },
      profiles: { ...(parsed.profiles || {}), ...(custom.profiles || {}) },
      debug: Boolean(custom.debug ?? parsed.debug),
      status: {
        loading: custom.status?.loading || '3D preview loading',
        updated: custom.status?.updated || '3D preview updated',
        unavailable: custom.status?.unavailable || '3D preview unavailable',
      },
    };
  } catch (error) {
    if (script.dataset.debug === 'true') {
      console.warn('[LUNQO 3D] Invalid JSON configuration; safe defaults will be used.', error);
    }
    return { productId: '', mapping: {}, profiles: {}, debug: false, status: {} };
  }
}

function getScope(element) {
  return element.closest('.shopify-section, dialog, product-card, quick-add-modal') || document.body;
}

function getState(script) {
  const config = parseConfig(script);
  const scope = getScope(script);
  let productStates = scopeStates.get(scope);
  if (!productStates) {
    productStates = new Map();
    scopeStates.set(scope, productStates);
  }
  const existing = productStates.get(config.productId);

  if (existing) {
    existing.config = config;
    return existing;
  }

  const state = { scope, config, selections: new Map(), revision: 0 };
  productStates.set(config.productId, state);
  return state;
}

function findConfig(root) {
  const productId = root.getAttribute('data-lunqo-3d-product-id') || '';
  const escapedProductId = escapeAttribute(productId);
  const local = root.parentElement?.querySelector(`${CONFIG_SELECTOR}[data-product-id="${escapedProductId}"]`);
  return local || getScope(root).querySelector(`${CONFIG_SELECTOR}[data-product-id="${escapedProductId}"]`);
}

function findMappedSlot(state, optionName, input) {
  const explicit = input?.getAttribute('data-lunqo-3d-slot');
  if (explicit && explicit !== FIXED_MATERIAL) return explicit;

  const normalizedOption = normalize(optionName);
  for (const [name, slot] of Object.entries(state.config.mapping)) {
    if (normalize(name) === normalizedOption && slot !== FIXED_MATERIAL) return String(slot);
  }

  const knownIndex =
    /\b(top|upper|primary|main)\b/.test(normalizedOption) ? 1 :
    /\b(bottom|base|lower|secondary)\b/.test(normalizedOption) ? 2 :
    /\b(watch|holder|third|custom|accent)\b/.test(normalizedOption) ? 3 :
    null;
  return knownIndex ? `${MATERIAL_SLOT_PREFIX}${knownIndex}` : null;
}

function selectedInputs(scope) {
  const native = [
    ...scope.querySelectorAll('variant-picker fieldset input:checked'),
    ...scope.querySelectorAll('variant-picker select'),
  ];
  const custom = [...scope.querySelectorAll('[data-lunqo-3d-option-name]:checked')];
  return [...native, ...custom];
}

function getInputData(input) {
  if (input instanceof HTMLSelectElement) {
    const option = input.selectedOptions[0];
    return {
      optionName: option?.dataset.optionName || input.name.replace(/^options\[|\]$/g, ''),
      value: option?.value || input.value,
      swatchOwner: input,
    };
  }

  return {
    optionName: input.getAttribute('data-lunqo-3d-option-name') || input.dataset.optionName || input.name,
    value: input.value,
    swatchOwner: input.closest('label') || input,
  };
}

function inferSwatchColor(owner) {
  const swatch = owner?.querySelector?.('.swatch');
  if (!swatch) return null;
  const inline = swatch.style.getPropertyValue('--swatch-background').trim();
  const computed = getComputedStyle(swatch).getPropertyValue('--swatch-background').trim();
  const candidate = inline || computed;
  return candidate && !candidate.includes('url(') ? validColor(candidate) : null;
}

function filamentColor(name) {
  const normalizedName = normalize(name);
  const canonicalName = FILAMENT_COLOR_ALIASES[normalizedName] || normalizedName;
  if (BAMBU_FILAMENT_COLORS[canonicalName]) return BAMBU_FILAMENT_COLORS[canonicalName];

  const match = Object.keys(BAMBU_FILAMENT_COLORS)
    .sort((left, right) => right.length - left.length)
    .find((colorName) => canonicalName.includes(colorName));
  return match ? BAMBU_FILAMENT_COLORS[match] : null;
}

function getProfile(state, value, owner) {
  const normalizedValue = normalize(value);
  let configured;
  for (const [name, profile] of Object.entries(state.config.profiles)) {
    if (normalize(name) === normalizedValue || normalize(profile?.name) === normalizedValue) {
      configured = profile;
      break;
    }
  }

  const color =
    validColor(configured?.color) ||
    filamentColor(value) ||
    validColor(owner?.getAttribute?.('data-lunqo-3d-color')) ||
    validColor(owner?.querySelector?.('[data-lunqo-3d-color]')?.getAttribute('data-lunqo-3d-color')) ||
    inferSwatchColor(owner);

  return {
    name: configured?.name || value,
    color,
    roughness: clamp(configured?.roughness, FALLBACK_ROUGHNESS),
    metallic: clamp(configured?.metallic, FALLBACK_METALLIC),
    texture: configured?.texture || null,
  };
}

function captureSelections(state) {
  const next = new Map(state.selections);

  for (const input of selectedInputs(state.scope)) {
    const { optionName, value, swatchOwner } = getInputData(input);
    const slot = findMappedSlot(state, optionName, input);
    if (!slot || !value) continue;
    next.set(slot, getProfile(state, value, swatchOwner));
  }

  state.selections = next;
  state.revision += 1;
  return state.revision;
}

function setStatus(root, type) {
  const status = root.parentElement?.querySelector('[data-lunqo-3d-status]');
  const configScript = findConfig(root);
  const config = configScript ? parseConfig(configScript) : null;
  if (!status || !config) return;

  status.textContent = config.status[type] || '';
  status.dataset.state = type;
  if (type === 'updated') {
    window.setTimeout(() => {
      if (status.dataset.state === 'updated') status.dataset.state = 'idle';
    }, 1400);
  }
}

function warn(state, message, detail) {
  if (state.config.debug) console.warn(`[LUNQO 3D] ${message}`, detail || '');
}

async function applyState(root, state, revision) {
  const viewer = root.querySelector('model-viewer');
  if (!viewer) return;

  if (!viewer.model?.materials) {
    setStatus(root, 'loading');
    return;
  }

  const materials = Array.from(viewer.model.materials);
  const names = new Set(materials.map((material) => material.name));
  let applied = 0;

  for (const [slot, profile] of state.selections) {
    if (revision !== state.revision) return;
    if (slot === FIXED_MATERIAL || !slot.startsWith(MATERIAL_SLOT_PREFIX)) continue;

    const material = materials.find((candidate) => candidate.name === slot);
    if (!material) continue;
    if (!profile.color) {
      warn(state, `No usable color value for ${profile.name}.`);
      continue;
    }

    const pbr = material.pbrMetallicRoughness;
    const colorFactor = colorToFactor(profile.color);
    if (!pbr?.setBaseColorFactor || !colorFactor) {
      warn(state, `Unable to convert ${profile.color} to a model-viewer color factor.`);
      continue;
    }
    pbr.setBaseColorFactor(colorFactor);
    pbr.setRoughnessFactor?.(profile.roughness);
    pbr.setMetallicFactor?.(profile.metallic);
    applied += 1;

    if (profile.texture) {
      warn(state, `Texture profile for ${profile.name} is reserved but not applied in v1.`);
    }
  }

  if (applied) {
    setStatus(root, 'updated');
  } else {
    if (state.config.debug) setStatus(root, 'unavailable');
    warn(state, 'No matching configurable material slots were found.', [...names]);
  }
}

function autoloadModel(root) {
  if (
    root.getAttribute('data-lunqo-3d-autoload') !== 'true' ||
    root.querySelector('model-viewer') ||
    root.hasAttribute('data-media-loaded')
  ) {
    return;
  }

  const load = () => {
    if (
      !root.isConnected ||
      root.querySelector('model-viewer') ||
      root.hasAttribute('data-media-loaded')
    ) {
      return;
    }

    if (typeof root.loadContent === 'function') {
      root.loadContent(false);
    }
  };

  if (customElements.get('product-model')) {
    load();
  } else {
    customElements.whenDefined('product-model').then(load);
  }
}

function initializeRoot(root) {
  if (!(root instanceof HTMLElement)) return;
  const script = findConfig(root);
  if (!script) return;
  const state = getState(script);
  const revision = captureSelections(state);
  autoloadModel(root);
  const viewer = root.querySelector('model-viewer');

  if (root.dataset.lunqo3dInitialized !== 'true') {
    root.dataset.lunqo3dInitialized = 'true';
  }

  if (viewer && viewer.dataset.lunqo3dBound !== 'true') {
    viewer.dataset.lunqo3dBound = 'true';
    viewer.addEventListener('load', () => applyState(root, state, state.revision));
  }

  applyState(root, state, revision);
}

function initializeAll() {
  scheduled = false;
  document.querySelectorAll(PRODUCT_MODEL_SELECTOR).forEach(initializeRoot);
}

function scheduleInitialize() {
  if (scheduled) return;
  scheduled = true;
  queueMicrotask(initializeAll);
}

function updateScope(scope) {
  for (const script of scope.querySelectorAll(CONFIG_SELECTOR)) {
    const state = getState(script);
    const revision = captureSelections(state);
    scope
      .querySelectorAll(
        `${PRODUCT_MODEL_SELECTOR}[data-lunqo-3d-product-id="${escapeAttribute(state.config.productId)}"]`
      )
      .forEach((root) => applyState(root, state, revision));
  }
}

document.addEventListener(
  'change',
  (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    if (!target.closest('variant-picker') && !target.matches('[data-lunqo-3d-option-name]')) return;
    updateScope(getScope(target));
  },
  true
);

document.addEventListener(
  StandardEvents.productSelect,
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const scope = getScope(target);
    updateScope(scope);
    event.promise?.then(scheduleInitialize, scheduleInitialize);
  },
  true
);

function containsInitializableNode(node) {
  if (!(node instanceof Element)) return false;
  return (
    node.matches(`${PRODUCT_MODEL_SELECTOR}, ${CONFIG_SELECTOR}, model-viewer`) ||
    Boolean(node.querySelector(`${PRODUCT_MODEL_SELECTOR}, ${CONFIG_SELECTOR}, model-viewer`))
  );
}

new MutationObserver((mutations) => {
  const shouldInitialize = mutations.some((mutation) =>
    Array.from(mutation.addedNodes).some(containsInitializableNode)
  );
  if (shouldInitialize) scheduleInitialize();
}).observe(document.documentElement, { childList: true, subtree: true });

scheduleInitialize();
